import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── Load Razorpay checkout script dynamically ────────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id  = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [packs, setPacks] = useState([]);
  const [subjects, setSubjects] = useState({ languageSubjects: [], domainSubjects: [] });
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedLanguageSubjects, setSelectedLanguageSubjects] = useState([]);
  const [selectedDomainSubjects, setSelectedDomainSubjects] = useState([]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Payment success state ──
  const [paymentSuccess, setPaymentSuccess] = useState(null); // null | { orderNo, packName, expireOn, amount, paymentId }

  // Customer details for payment
  const [customerDetails, setCustomerDetails] = useState({
    name:   user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '',
    email:  user?.email  || '',
    mobile: user?.phone  || '',
  });

  useEffect(() => { fetchCourses(); }, []);

  useEffect(() => {
    if (user) {
      setCustomerDetails({
        name:   `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email:  user.email || '',
        mobile: user.phone || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (selectedCourse) {
      fetchPacks(selectedCourse);
      fetchSubjects(selectedCourse);
      setSelectedPack(null);
      setSelectedLanguageSubjects([]);
      setSelectedDomainSubjects([]);
      setShowConfigModal(false);
    } else {
      setPacks([]);
      setSubjects({ languageSubjects: [], domainSubjects: [] });
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/subscriptions/courses');
      if (res.data.success) setCourses(res.data.data);
    } catch {}
  };

  const fetchPacks = async (courseId) => {
    setLoading(true);
    try {
      const res = await api.get(`/subscriptions/packs/${courseId}`);
      if (res.data.success) setPacks(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchSubjects = async (courseId) => {
    try {
      const res = await api.get(`/subscriptions/subjects/${courseId}`);
      if (res.data.success) setSubjects(res.data.data);
    } catch {}
  };

  const formatValidity = (days) => {
    if (!days) return '-';
    const months = Math.round(days / 30);
    if (months >= 12) {
      const y = Math.floor(months / 12);
      const m = months % 12;
      return m === 0 ? `${y} Year${y > 1 ? 's' : ''}` : `${y} Year${y > 1 ? 's' : ''} ${m} Month${m > 1 ? 's' : ''}`;
    }
    return `${months} Month${months > 1 ? 's' : ''}`;
  };

  const formatPrice = (price) =>
    !price ? '₹0' : `₹${Number(price).toLocaleString('en-IN')}`;


  // Compute live total for variable-priced packs
  const computeVariablePrice = (pack, langSubjects, domainSubjects) => {
    if (!pack?.IsPackPriceVariable) {
      return parseFloat(pack?.DiscountedPrice || pack?.ActualPrice || 0);
    }
    const domainCount = domainSubjects.length;
    const langCount   = langSubjects.length;
    const extraDomain = Math.max(0, domainCount - (pack.DefaultDomainSubjCount || 0));
    const extraLang   = Math.max(0, langCount   - (pack.DefaultLangSubjCount   || 0));
    return (extraDomain * parseFloat(pack.VarPckPricePerDomainSubj || 0))
         + (extraLang   * parseFloat(pack.VarPckPricePerLangSubj   || 0));
  };

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setSelectedLanguageSubjects([]);
    setSelectedDomainSubjects([]);
    setShowConfigModal(true);
  };

  const toggleLanguageSubject = (topicId) => {
    const max = selectedPack?.LangSubjMaxSelectCount || 0;
    if (selectedLanguageSubjects.includes(topicId)) {
      setSelectedLanguageSubjects(p => p.filter(id => id !== topicId));
    } else {
      if (max > 0 && selectedLanguageSubjects.length >= max) {
        alert(`You can select maximum ${max} language subject(s) for this pack.`);
        return;
      }
      setSelectedLanguageSubjects(p => [...p, topicId]);
    }
  };

  const toggleDomainSubject = (topicId) => {
    const max = selectedPack?.DomainSubjMaxSelectCount || 0;
    if (selectedDomainSubjects.includes(topicId)) {
      setSelectedDomainSubjects(p => p.filter(id => id !== topicId));
    } else {
      if (max > 0 && selectedDomainSubjects.length >= max) {
        alert(`You can select maximum ${max} domain subject(s) for this pack.`);
        return;
      }
      setSelectedDomainSubjects(p => [...p, topicId]);
    }
  };

  const handleProceedToPayment = () => {
    setShowConfigModal(false);
    setShowPaymentModal(true);
  };

  // ─── RAZORPAY PAYMENT FLOW ────────────────────────────────────────────────
  const handlePayment = async () => {
    // Guard: variable-priced pack with no subjects selected
    if (selectedPack?.IsPackPriceVariable) {
      const totalSubjects = selectedLanguageSubjects.length + selectedDomainSubjects.length;
      if (totalSubjects === 0) {
        alert('Please select at least one subject before proceeding to payment.');
        setShowPaymentModal(false);
        setShowConfigModal(true);
        return;
      }
    }
    if (!customerDetails.name || !customerDetails.email || !customerDetails.mobile) {
      alert('Please fill in all customer details');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email)) {
      alert('Please enter a valid email address');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(customerDetails.mobile)) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }

    setSubmitting(true);
    try {
      // 1 — Load Razorpay checkout script
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert('Failed to load payment gateway. Please check your internet connection and try again.');
        setSubmitting(false);
        return;
      }

      // 2 — Create Razorpay order on server
      const orderRes = await api.post('/payment/create-order', {
        subscriptionId:  selectedPack.SubscriptionId,
        courseId:        selectedCourse,
        languageSubjects: selectedLanguageSubjects,
        domainSubjects:   selectedDomainSubjects,
      });

      if (!orderRes.data.success) {
        alert(orderRes.data.message || 'Error creating payment order');
        setSubmitting(false);
        return;
      }

      const {
        orderId, amount, currency, keyId,
        finalPrice, subscriptionId, courseId,
        languageSubjects, domainSubjects,
      } = orderRes.data.data;

      // 3 — Open Razorpay checkout modal
      const options = {
        key:         keyId,
        amount,
        currency,
        name:        'BESST',
        description: selectedPack.PackTitle || selectedPack.SubscriptionName,
        order_id:    orderId,
        prefill: {
          name:    customerDetails.name,
          email:   customerDetails.email,
          contact: customerDetails.mobile,
        },
        theme: { color: '#6366f1' },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
        handler: async (response) => {
          // 4 — Payment succeeded — verify on server
          try {
            const verifyRes = await api.post('/payment/verify', {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              subscriptionId,
              courseId,
              languageSubjects,
              domainSubjects,
              finalPrice,
            });

            if (verifyRes.data.success) {
              // Close payment modal and show success screen
              setShowPaymentModal(false);
              setPaymentSuccess(verifyRes.data.data);
            } else {
              alert('Payment recorded but verification failed. Please contact support with Payment ID: ' + response.razorpay_payment_id);
            }
          } catch (err) {
            alert(
              'Payment was deducted but we could not confirm your order. ' +
              'Please contact support with Payment ID: ' + response.razorpay_payment_id
            );
          } finally {
            setSubmitting(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        alert(`Payment failed: ${response.error.description}. Please try again.`);
        setSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      console.error('Payment initiation error:', err);
      alert(err.response?.data?.message || 'Error initiating payment. Please try again.');
      setSubmitting(false);
    }
  };

  // ─── SUCCESS SCREEN ───────────────────────────────────────────────────────
  if (paymentSuccess) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="max-w-lg w-full mx-auto px-8">
          <div className="glass-card text-center">
            {/* Animated checkmark */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                  <div className="w-16 h-16 rounded-full bg-green-500/30 flex items-center justify-center">
                    <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-extrabold text-text-light mb-2">Payment Successful!</h2>
            <p className="text-text-muted mb-8">Your subscription has been activated. Get started with your preparation!</p>

            {/* Receipt */}
            <div className="bg-bg-secondary rounded-2xl p-6 text-left space-y-3 mb-8 border border-glass-border">
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Order No</span>
                <span className="text-text-light font-mono font-semibold">{paymentSuccess.orderNo}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Pack</span>
                <span className="text-text-light font-semibold">{paymentSuccess.packName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Valid Until</span>
                <span className="text-green-400 font-semibold">
                  {new Date(paymentSuccess.expireOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
              <hr className="border-glass-border" />
              <div className="flex justify-between">
                <span className="text-text-muted font-semibold">Amount Paid</span>
                <span className="text-accent-gold font-bold text-lg">{formatPrice(paymentSuccess.amount)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-text-muted">Payment ID</span>
                <span className="text-text-muted font-mono">{paymentSuccess.paymentId}</span>
              </div>
            </div>

            <div className="flex gap-4">
              <a
                href="/dashboard"
                className="flex-1 btn-primary py-3 text-center rounded-xl font-semibold"
              >
                Go to Dashboard →
              </a>
              <button
                onClick={() => {
                  setPaymentSuccess(null);
                  setSelectedPack(null);
                  setSelectedCourse('');
                }}
                className="flex-1 py-3 px-6 border border-glass-border rounded-xl text-text-light hover:bg-white/5 transition-colors font-semibold"
              >
                Buy Another Pack
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── CONFIGURATION MODAL ──────────────────────────────────────────────────
  const ConfigurationModal = () => {
    if (!showConfigModal || !selectedPack) return null;
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] border border-glass-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
          <div className="p-6 border-b border-glass-border bg-gradient-to-r from-primary/10 to-accent-gold/10 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-text-light">Configure Your Subscription</h3>
                <p className="text-sm text-text-muted mt-1">
                  {selectedPack.SubscriptionName} • {formatValidity(selectedPack.PackValidity)}
                </p>
              </div>
              <button onClick={() => setShowConfigModal(false)} className="text-text-muted hover:text-text-light text-3xl leading-none p-2 hover:bg-white/10 rounded-lg transition-colors">&times;</button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-4 mb-6">
              {subjects.languageSubjects.length > 0 && selectedPack.LangSubjMaxSelectCount > 0 && (
                <div className="flex items-center justify-between p-4 bg-bg-secondary rounded-xl border border-glass-border">
                  <div>
                    <h4 className="font-semibold text-text-light flex items-center gap-2"><span className="text-blue-400">🌐</span> Language Subjects</h4>
                    <p className="text-sm text-text-muted mt-1">Selected: <span className="text-primary font-semibold">{selectedLanguageSubjects.length}</span> / {selectedPack.LangSubjMaxSelectCount}</p>
                  </div>
                  <button onClick={() => setShowLanguageModal(true)} className="px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-xl font-semibold transition-colors border border-blue-500/30">Select Subjects</button>
                </div>
              )}
              {subjects.domainSubjects.length > 0 && selectedPack.DomainSubjMaxSelectCount > 0 && (
                <div className="flex items-center justify-between p-4 bg-bg-secondary rounded-xl border border-glass-border">
                  <div>
                    <h4 className="font-semibold text-text-light flex items-center gap-2"><span className="text-green-400">📚</span> Domain Subjects</h4>
                    <p className="text-sm text-text-muted mt-1">Selected: <span className="text-green-400 font-semibold">{selectedDomainSubjects.length}</span> / {selectedPack.DomainSubjMaxSelectCount}</p>
                  </div>
                  <button onClick={() => setShowDomainModal(true)} className="px-6 py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-xl font-semibold transition-colors border border-green-500/30">Select Subjects</button>
                </div>
              )}
            </div>

            {(selectedLanguageSubjects.length > 0 || selectedDomainSubjects.length > 0) && (
              <div className="mb-6 space-y-4">
                {selectedLanguageSubjects.length > 0 && (
                  <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                    <h5 className="text-sm font-semibold text-blue-400 mb-2">Selected Language Subjects:</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedLanguageSubjects.map(id => {
                        const s = subjects.languageSubjects.find(x => x.TopicId === id);
                        return s ? <span key={id} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">{s.TopicName}</span> : null;
                      })}
                    </div>
                  </div>
                )}
                {selectedDomainSubjects.length > 0 && (
                  <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                    <h5 className="text-sm font-semibold text-green-400 mb-2">Selected Domain Subjects:</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedDomainSubjects.map(id => {
                        const s = subjects.domainSubjects.find(x => x.TopicId === id);
                        return s ? <span key={id} className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">{s.TopicName}</span> : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-bg-card p-6 rounded-xl border border-glass-border">
              <h4 className="font-semibold text-text-light mb-4 text-lg">Order Summary</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-text-muted">Pack:</span><span className="text-text-light font-medium">{selectedPack.SubscriptionName}</span></div>
                <div className="flex justify-between"><span className="text-text-muted">Validity:</span><span className="text-text-light">{formatValidity(selectedPack.PackValidity)}</span></div>
                <div className="flex justify-between"><span className="text-text-muted">Language Subjects:</span><span className="text-text-light">{selectedLanguageSubjects.length} / {selectedPack.LangSubjMaxSelectCount || 0}</span></div>
                <div className="flex justify-between"><span className="text-text-muted">Domain Subjects:</span><span className="text-text-light">{selectedDomainSubjects.length} / {selectedPack.DomainSubjMaxSelectCount || 0}</span></div>
                <hr className="border-glass-border my-4" />
                <div className="flex justify-between text-xl font-bold">
                  <span className="text-text-light">Total:</span>
                  <span className="text-accent-gold">
                    {selectedPack.IsPackPriceVariable
                      ? (selectedLanguageSubjects.length + selectedDomainSubjects.length) === 0
                        ? <span className="text-text-muted text-base font-normal">Select subjects to see price</span>
                        : formatPrice(computeVariablePrice(selectedPack, selectedLanguageSubjects, selectedDomainSubjects))
                      : formatPrice(selectedPack.DiscountedPrice || selectedPack.ActualPrice)
                    }
                  </span>
                </div>
                {selectedPack.IsPackPriceVariable && (selectedLanguageSubjects.length + selectedDomainSubjects.length) > 0 && (
                  <div className="text-xs text-text-muted text-right">
                    {selectedDomainSubjects.length + selectedLanguageSubjects.length} subject{(selectedDomainSubjects.length + selectedLanguageSubjects.length) > 1 ? 's' : ''} × {formatPrice(selectedPack.VarPckPricePerDomainSubj)} each
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-glass-border bg-bg-secondary/30 flex-shrink-0">
            <div className="flex gap-4">
              <button onClick={() => setShowConfigModal(false)} className="flex-1 py-3 px-6 border border-glass-border rounded-xl text-text-light hover:bg-white/5 transition-colors">Cancel</button>
              <button onClick={handleProceedToPayment} className="flex-1 btn-primary py-3">Proceed to Payment →</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── LANGUAGE SUBJECTS MODAL ──────────────────────────────────────────────
  const LanguageSubjectsModal = () => {
    if (!showLanguageModal) return null;
    const maxSelect   = selectedPack?.LangSubjMaxSelectCount || 0;
    const isMaxReached = maxSelect > 0 && selectedLanguageSubjects.length >= maxSelect;
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] border border-glass-border rounded-2xl w-full max-w-xl max-h-[85vh] overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-glass-border bg-gradient-to-r from-blue-500/10 to-purple-500/10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-text-light">Select Language Subjects</h3>
                <p className="text-sm text-text-muted mt-1">Choose your preferred language subjects</p>
              </div>
              <button onClick={() => setShowLanguageModal(false)} className="text-text-muted hover:text-text-light text-3xl leading-none p-2 hover:bg-white/10 rounded-lg transition-colors">&times;</button>
            </div>
          </div>
          <div className="px-6 py-4 bg-bg-secondary/50 border-b border-glass-border">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Selected:</span>
              <span className={`font-bold text-lg ${isMaxReached ? 'text-orange-400' : 'text-primary'}`}>{selectedLanguageSubjects.length} / {maxSelect || 'Unlimited'}</span>
            </div>
            {isMaxReached && <p className="text-orange-400 text-sm mt-2">⚠️ Maximum selection limit reached.</p>}
          </div>
          <div className="p-6 max-h-[45vh] overflow-y-auto">
            {subjects.languageSubjects.length === 0 ? (
              <p className="text-text-muted text-center py-8">No language subjects available.</p>
            ) : (
              <div className="space-y-3">
                {subjects.languageSubjects.map((subject) => {
                  const isSelected = selectedLanguageSubjects.includes(subject.TopicId);
                  const isDisabled = !isSelected && isMaxReached;
                  return (
                    <label key={subject.TopicId} className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${isSelected ? 'bg-primary/20 border-2 border-primary' : isDisabled ? 'bg-bg-card/50 border-2 border-glass-border opacity-50 cursor-not-allowed' : 'bg-bg-card border-2 border-glass-border hover:border-primary/50'}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => !isDisabled && toggleLanguageSubject(subject.TopicId)} disabled={isDisabled} className="w-5 h-5 rounded" />
                      <span className={`font-medium ${isSelected ? 'text-primary' : 'text-text-light'}`}>{subject.TopicName}</span>
                      {isSelected && <span className="ml-auto text-primary text-xl">✓</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-glass-border bg-bg-secondary/30">
            <button onClick={() => setShowLanguageModal(false)} className="w-full btn-primary py-4 text-lg font-semibold">Done ({selectedLanguageSubjects.length} selected)</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── DOMAIN SUBJECTS MODAL ────────────────────────────────────────────────
  const DomainSubjectsModal = () => {
    if (!showDomainModal) return null;
    const maxSelect    = selectedPack?.DomainSubjMaxSelectCount || 0;
    const isMaxReached = maxSelect > 0 && selectedDomainSubjects.length >= maxSelect;
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] border border-glass-border rounded-2xl w-full max-w-xl max-h-[85vh] overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-glass-border bg-gradient-to-r from-green-500/10 to-teal-500/10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-text-light">Select Domain Subjects</h3>
                <p className="text-sm text-text-muted mt-1">Choose your domain-specific subjects</p>
              </div>
              <button onClick={() => setShowDomainModal(false)} className="text-text-muted hover:text-text-light text-3xl leading-none p-2 hover:bg-white/10 rounded-lg transition-colors">&times;</button>
            </div>
          </div>
          <div className="px-6 py-4 bg-bg-secondary/50 border-b border-glass-border">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Selected:</span>
              <span className={`font-bold text-lg ${isMaxReached ? 'text-orange-400' : 'text-green-400'}`}>{selectedDomainSubjects.length} / {maxSelect || 'Unlimited'}</span>
            </div>
            {isMaxReached && <p className="text-orange-400 text-sm mt-2">⚠️ Maximum selection limit reached.</p>}
          </div>
          <div className="p-6 max-h-[45vh] overflow-y-auto">
            {subjects.domainSubjects.length === 0 ? (
              <p className="text-text-muted text-center py-8">No domain subjects available.</p>
            ) : (
              <div className="space-y-3">
                {subjects.domainSubjects.map((subject) => {
                  const isSelected = selectedDomainSubjects.includes(subject.TopicId);
                  const isDisabled = !isSelected && isMaxReached;
                  return (
                    <label key={subject.TopicId} className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${isSelected ? 'bg-green-500/20 border-2 border-green-500' : isDisabled ? 'bg-bg-card/50 border-2 border-glass-border opacity-50 cursor-not-allowed' : 'bg-bg-card border-2 border-glass-border hover:border-green-500/50'}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => !isDisabled && toggleDomainSubject(subject.TopicId)} disabled={isDisabled} className="w-5 h-5 rounded" />
                      <span className={`font-medium ${isSelected ? 'text-green-400' : 'text-text-light'}`}>{subject.TopicName}</span>
                      {isSelected && <span className="ml-auto text-green-400 text-xl">✓</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-glass-border bg-bg-secondary/30">
            <button onClick={() => setShowDomainModal(false)} className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300">
              Done ({selectedDomainSubjects.length} selected)
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="text-center mb-12">
          <div className="hero-badge mx-auto mb-6">💎 Premium Plans</div>
          <h1 className="text-4xl lg:text-5xl font-extrabold gradient-text mb-6">Choose Your Subscription</h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">Select a course and find the perfect subscription plan for your exam preparation journey.</p>
        </div>

        {/* Course Selector */}
        <div className="glass-card max-w-xl mx-auto mb-12">
          <label className="block text-text-light font-semibold mb-3">Select Your Course</label>
          <select
            className="w-full bg-[#1a0b2e] border border-white/20 rounded-lg p-3 text-white focus:border-accent-gold outline-none appearance-none cursor-pointer"
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
          >
            <option value="0">-Select Course-</option>
            {courses.map(course => (
              <option key={course.CourseId} value={course.CourseId} className="bg-white text-black">
                {course.CourseName}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="text-text-muted mt-4">Loading subscription packs...</p>
          </div>
        )}

        {!loading && packs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-text-light text-center mb-8">Available Subscription Packs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packs.map((pack) => (
                <div
                  key={pack.SubscriptionId}
                  className={`glass-card relative transition-all duration-300 cursor-pointer ${selectedPack?.SubscriptionId === pack.SubscriptionId ? 'border-primary ring-2 ring-primary/50' : 'hover:border-primary/50'}`}
                  onClick={() => handleSelectPack(pack)}
                >
                  {pack.Discount > 0 && (
                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">{pack.Discount}% OFF</div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-text-light mb-2">{pack.SubscriptionName}</h3>
                    {pack.PackTitle && <p className="text-text-muted text-sm">{pack.PackTitle}</p>}
                  </div>
                  <div className="text-center mb-6">
                    {pack.IsPackPriceVariable ? (
                      <div>
                        <div className="text-3xl font-extrabold text-accent-gold">
                          {formatPrice(pack.VarPckPricePerDomainSubj)}
                          <span className="text-base font-normal text-text-muted"> / subject</span>
                        </div>
                        <p className="text-xs text-text-muted mt-1">Total depends on subjects selected</p>
                      </div>
                    ) : pack.DiscountedPrice && pack.DiscountedPrice < pack.ActualPrice ? (
                      <>
                        <span className="text-text-muted line-through text-lg">{formatPrice(pack.ActualPrice)}</span>
                        <div className="text-3xl font-extrabold text-accent-gold">{formatPrice(pack.DiscountedPrice)}</div>
                      </>
                    ) : (
                      <div className="text-3xl font-extrabold text-accent-gold">{formatPrice(pack.ActualPrice)}</div>
                    )}
                  </div>
                  <div className="text-center mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-primary">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatValidity(pack.PackValidity)}
                    </span>
                  </div>
                  {pack.features && pack.features.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {pack.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-text-muted">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span dangerouslySetInnerHTML={{ __html: feature }} />
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="bg-bg-secondary/50 rounded-xl p-4 mb-6 space-y-2 text-sm">
                    {pack.LangSubjMaxSelectCount > 0 && (
                      <div className="flex items-center gap-2 text-text-muted">
                        <span className="text-blue-400">🌐</span> Max {pack.LangSubjMaxSelectCount} Language Subject{pack.LangSubjMaxSelectCount > 1 ? 's' : ''}
                      </div>
                    )}
                    {pack.DomainSubjMaxSelectCount > 0 && (
                      <div className="flex items-center gap-2 text-text-muted">
                        <span className="text-green-400">📚</span> Max {pack.DomainSubjMaxSelectCount} Domain Subject{pack.DomainSubjMaxSelectCount > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  <button
                    className="w-full py-3 rounded-xl font-semibold transition-colors bg-primary text-white hover:bg-primary/80"
                    onClick={(e) => { e.stopPropagation(); handleSelectPack(pack); }}
                  >
                    Select &amp; Configure
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && selectedCourse && packs.length === 0 && (
          <div className="text-center py-12 glass-card">
            <p className="text-text-muted text-lg">No subscription packs available for this course.</p>
          </div>
        )}

        <ConfigurationModal />
        <LanguageSubjectsModal />
        <DomainSubjectsModal />
        {showPaymentModal && selectedPack && (() => {
          const finalPrice = selectedPack?.IsPackPriceVariable
            ? computeVariablePrice(selectedPack, selectedLanguageSubjects, selectedDomainSubjects)
            : parseFloat(selectedPack?.DiscountedPrice || selectedPack?.ActualPrice || 0);
          return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] border border-glass-border rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-glass-border bg-gradient-to-r from-accent-gold/10 to-orange-500/10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-text-light">Complete Your Purchase</h3>
                <p className="text-sm text-text-muted mt-1">Confirm your details and pay securely</p>
              </div>
              <button onClick={() => setShowPaymentModal(false)} className="text-text-muted hover:text-text-light text-3xl leading-none p-2 hover:bg-white/10 rounded-lg transition-colors">&times;</button>
            </div>
          </div>

          <div className="p-6">
            {/* Order Summary */}
            <div className="bg-bg-secondary rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-text-light mb-3">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-text-muted">Pack:</span><span className="text-text-light">{selectedPack?.SubscriptionName}</span></div>
                <div className="flex justify-between"><span className="text-text-muted">Validity:</span><span className="text-text-light">{formatValidity(selectedPack?.PackValidity)}</span></div>
                <hr className="border-glass-border my-2" />
                {selectedPack?.IsPackPriceVariable && (
                  <div className="flex justify-between">
                    <span className="text-text-muted">Subjects:</span>
                    <span className="text-text-light">
                      {selectedDomainSubjects.length + selectedLanguageSubjects.length} × {formatPrice(selectedPack?.VarPckPricePerDomainSubj)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-text-light">Total Amount:</span>
                  <span className="text-accent-gold">{formatPrice(finalPrice)}</span>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-light mb-2">Full Name *</label>
                <input
                  type="text"
                  value={customerDetails.name}
                  onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                  className="form-input"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-light mb-2">Email Address *</label>
                <input
                  type="email"
                  value={customerDetails.email}
                  onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                  className="form-input"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-light mb-2">Mobile Number *</label>
                <input
                  type="tel"
                  value={customerDetails.mobile}
                  onChange={(e) => setCustomerDetails({ ...customerDetails, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="form-input"
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                />
              </div>
            </div>

            {/* Razorpay badge */}
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <p className="text-blue-300 font-semibold text-sm">100% Secure Payment</p>
                  <p className="text-text-muted text-xs">Powered by Razorpay · UPI, Cards, Net Banking &amp; Wallets accepted</p>
                </div>
                {/* Razorpay logo text */}
                <div className="ml-auto">
                  <span className="text-[#3395FF] font-bold text-lg tracking-tight">razorpay</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-glass-border bg-bg-secondary/30">
            <div className="flex gap-4">
              <button
                onClick={() => { setShowPaymentModal(false); setShowConfigModal(true); }}
                className="flex-1 py-3 px-6 border border-glass-border rounded-xl text-text-light hover:bg-white/5 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handlePayment}
                disabled={submitting}
                className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    Opening Payment...
                  </span>
                ) : (
                  `Pay ${formatPrice(finalPrice)}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SubscriptionPage;